import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Search, LayoutDashboard, History, Sun, Moon, Bell } from 'lucide-react';

const steps = [
  {
    icon: '🚀',
    title: 'Welcome to iLeadX!',
    description: 'Your AI-powered lead generation platform is ready. Let us show you around in less than 2 minutes.',
    action: null,
  },
  {
    icon: <Search size={28} color="#f78166" />,
    title: 'Find Your Leads',
    description: 'Enter what you are looking for and what you sell. iLeadX scrapes Google Maps, enriches businesses with contact details, and scores them with AI.',
    action: { label: 'Go to New Search', path: '/dashboard/new-search' },
  },
  {
    icon: <LayoutDashboard size={28} color="#a371f7" />,
    title: 'Your Dashboard',
    description: 'Track your lead generation analytics,  total leads, AI scores, industry breakdowns, and weekly trends.',
    action: { label: 'View Dashboard', path: '/dashboard' },
  },
  {
    icon: <History size={28} color="#3fb950" />,
    title: 'Lead History',
    description: 'All your previously generated leads live here. Click any company to see the full profile with AI insights, pitch angles, and contact details.',
    action: { label: 'Open History', path: '/dashboard/history' },
  },
  {
    icon: '🌓',
    title: 'Theme Toggle',
    description: 'Switch between dark and light mode anytime. Click the sun/moon icon in the top bar,  your preference is saved automatically.',
    action: null,
  },
  {
    icon: <Bell size={28} color="#d29922" />,
    title: 'Notifications',
    description: 'When a search completes or you export leads, you will get notified here. Never miss an update.',
    action: null,
  },
  {
    icon: '🎉',
    title: 'You Are All Set!',
    description: 'Start your first search and discover how easy lead generation can be. Happy prospecting!',
    action: { label: 'Start Searching', path: '/dashboard/new-search' },
  },
];

export default function OnboardingTour({ onComplete }) {
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState('next'); // 'next' | 'prev'
  const [exiting, setExiting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const goToStep = useCallback((step) => {
    setDirection(step > currentStep ? 'next' : 'prev');
    setCurrentStep(step);
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      goToStep(currentStep + 1);
      // Navigate in background if there's an action for the next step
      const nextStep = steps[currentStep + 1];
      if (nextStep.action?.path) {
        navigate(nextStep.action.path);
      }
    } else {
      handleFinish();
    }
  }, [currentStep, goToStep, navigate]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
      const prevStep = steps[currentStep - 1];
      if (prevStep.action?.path) {
        navigate(prevStep.action.path);
      }
    }
  }, [currentStep, goToStep, navigate]);

  const handleFinish = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      if (onComplete) onComplete();
    }, 300);
  }, [onComplete]);

  const handleActionClick = useCallback((path) => {
    if (path) navigate(path);
  }, [navigate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleFinish();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleFinish, handleNext, handlePrev]);

  if (!visible) return null;

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(2px)',
      animation: 'fadeIn 0.3s ease',
      padding: 20,
    }}>
      <div style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: 20,
        width: '100%',
        maxWidth: 480,
        boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5)',
        overflow: 'hidden',
        animation: exiting ? 'scaleOut 0.3s ease forwards' : 'scaleIn 0.3s ease',
      }}>
        {/* Close button */}
        <button
          onClick={handleFinish}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'var(--bg-tertiary)',
            border: 'none',
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            zIndex: 1,
          }}
        >
          <X size={16} />
        </button>

        {/* Content area with slide animation */}
        <div style={{ position: 'relative', overflow: 'hidden', minHeight: 260 }}>
          <div
            key={currentStep}
            style={{
              padding: '40px 36px 24px',
              textAlign: 'center',
              animation: direction === 'next' ? 'slideInRight 0.35s ease' : 'slideInLeft 0.35s ease',
            }}
          >
            {/* Icon */}
            <div style={{
              width: 72,
              height: 72,
              borderRadius: 20,
              background: 'var(--bg-tertiary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: 32,
            }}>
              {step.icon}
            </div>

            {/* Title */}
            <h2 style={{
              color: 'var(--text-primary)',
              fontSize: 'var(--text-3xl)',
              fontWeight: 700,
              margin: '0 0 12px',
              letterSpacing: '-0.02em',
            }}>
              {step.title}
            </h2>

            {/* Description */}
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-base)',
              lineHeight: 1.7,
              margin: '0 0 8px',
            }}>
              {step.description}
            </p>

            {/* Action button */}
            {step.action && (
              <button
                onClick={() => handleActionClick(step.action.path)}
                style={{
                  background: 'none',
                  border: '1px solid var(--accent-primary)',
                  color: 'var(--accent-primary)',
                  padding: '8px 20px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  marginTop: 12,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--accent-glow)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                }}
              >
                {step.action.label}
              </button>
            )}
          </div>
        </div>

        {/* Footer: Dots + Navigation */}
        <div style={{
          padding: '16px 36px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          {/* Skip button (left) */}
          <button
            onClick={handleFinish}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 500,
              padding: 0,
            }}
          >
            Skip
          </button>

          {/* Dots (center) */}
          <div style={{ display: 'flex', gap: 6 }}>
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => goToStep(i)}
                style={{
                  width: i === currentStep ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  border: 'none',
                  background: i === currentStep ? 'var(--accent-primary)' : 'var(--border-primary)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>

          {/* Next/Finish button (right) */}
          <div style={{ display: 'flex', gap: 8 }}>
            {!isFirst && (
              <button
                onClick={handlePrev}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: '1px solid var(--border-primary)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--accent-primary)';
                  e.currentTarget.style.color = 'var(--accent-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-primary)';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                padding: '8px 20px',
                borderRadius: 10,
                border: 'none',
                background: 'var(--accent-gradient)',
                color: 'white',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: 'var(--shadow-sm)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.boxShadow = 'var(--shadow-glow)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              }}
            >
              {isLast ? 'Finish' : 'Next'}
              {!isLast && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}